import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Upload, Trash2, CheckCircle2, Circle, Loader2, Music as MusicIcon, Edit, Save, X } from 'lucide-react';
import {
  fetchMusic,
  uploadMusic,
  activateMusic,
  renameMusic,
  deleteMusic,
  resolveImageUrl,
  formatApiError,
} from '../../services/api';

const MusicManager = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMusic();
      setTracks(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!newTitle.trim()) {
      setError('Please enter a track title before choosing a file.');
      return;
    }
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      await uploadMusic(file, newTitle.trim(), setProgress);
      setNewTitle('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateMusic(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this track? This cannot be undone.')) return;
    try {
      await deleteMusic(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const startRename = (track) => {
    setEditingId(track.id);
    setEditingTitle(track.title);
  };

  const saveRename = async () => {
    if (!editingTitle.trim()) return;
    try {
      await renameMusic(editingId, editingTitle.trim());
      setEditingId(null);
      setEditingTitle('');
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(2)} MB`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Background Music</h2>
          <p className="text-amber-100/60 text-sm mt-1">
            {tracks.length} track{tracks.length !== 1 ? 's' : ''} — the active track plays site-wide
          </p>
        </div>
      </div>

      <div className="mb-8 p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg space-y-4">
        <h3 className="text-xl font-semibold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Upload New Track</h3>

        <div>
          <Label className="text-amber-300 text-xs uppercase tracking-wider">Title *</Label>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g., Mystic Forest Melody"
            className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
            data-testid="music-title-input"
          />
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !newTitle.trim()}
            className="btn-gold"
            data-testid="music-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? `Uploading... ${progress}%` : 'Choose Audio File'}
          </Button>
          <p className="text-xs text-amber-100/60">MP3, WAV, OGG, M4A, AAC, FLAC — max 100 MB</p>
        </div>

        {uploading ? (
          <div className="w-full bg-emerald-950/80 rounded-full h-2 overflow-hidden">
            <div className="bg-amber-500 h-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        ) : null}

        {error ? <div className="p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div> : null}
      </div>

      {loading ? (
        <p className="text-amber-100/60 text-center py-12">Loading tracks...</p>
      ) : tracks.length === 0 ? (
        <div className="text-center py-12 text-amber-100/60 border border-dashed border-amber-600/30 rounded-lg">
          <MusicIcon className="w-12 h-12 text-amber-400/50 mx-auto mb-3" />
          No music tracks yet. Upload one above to set the site's mystical mood.
        </div>
      ) : (
        <div className="space-y-3">
          {tracks.map((track) => (
            <div
              key={track.id}
              className={`flex items-center gap-4 p-4 bg-emerald-950/30 border rounded-lg transition ${
                track.is_active ? 'border-amber-400/70 shadow-[0_0_20px_rgba(217,178,67,0.3)]' : 'border-amber-600/20'
              }`}
              data-testid={`music-row-${track.id}`}
            >
              <MusicIcon className={`w-6 h-6 flex-shrink-0 ${track.is_active ? 'text-amber-400' : 'text-amber-300/60'}`} />

              <div className="flex-1 min-w-0">
                {editingId === track.id ? (
                  <div className="flex gap-2 items-center">
                    <Input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                      data-testid={`music-edit-input-${track.id}`}
                    />
                    <Button size="sm" onClick={saveRename} className="btn-gold" data-testid={`music-save-${track.id}`}>
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => setEditingId(null)} className="btn-mystical">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-amber-200 font-semibold truncate" style={{ fontFamily: 'Cinzel, serif' }}>
                      {track.title}
                      {track.is_active ? <span className="ml-2 text-xs text-amber-400 uppercase tracking-widest">· Active</span> : null}
                    </h3>
                    <p className="text-xs text-amber-100/50 mt-1">{formatSize(track.size)}</p>
                    <audio controls src={resolveImageUrl(track.url)} className="mt-2 h-8 max-w-xs" preload="none" />
                  </>
                )}
              </div>

              <div className="flex gap-2 flex-shrink-0">
                {!track.is_active && editingId !== track.id ? (
                  <Button
                    size="sm"
                    onClick={() => handleActivate(track.id)}
                    className="btn-gold"
                    data-testid={`music-activate-${track.id}`}
                  >
                    <Circle className="w-4 h-4 mr-1" />
                    Activate
                  </Button>
                ) : track.is_active && editingId !== track.id ? (
                  <span className="text-amber-400 flex items-center gap-1 px-3 py-2 text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Active
                  </span>
                ) : null}
                {editingId !== track.id ? (
                  <Button
                    size="sm"
                    onClick={() => startRename(track)}
                    className="btn-mystical"
                    data-testid={`music-rename-${track.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  onClick={() => handleDelete(track.id)}
                  className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30"
                  data-testid={`music-delete-${track.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicManager;
